/**
 * Built-in deployment stages offered anywhere an application environment is
 * created or edited. A stage is shared across an organization, so the same
 * stable identity can map a multi-application run to one environment per app.
 *
 * Custom values entered by the user are persisted to the
 * `org_environment_kinds` table and merged with these built-ins when the
 * dropdown is rendered. The historical "environment kind" name remains in the
 * schema/API for compatibility; product UI calls these values "Stages".
 */
export const BUILTIN_ENVIRONMENT_KINDS = ['production', 'staging', 'dev'] as const;

export type BuiltinEnvironmentKind = (typeof BUILTIN_ENVIRONMENT_KINDS)[number];

/** Product-facing stage backed by an org environment-kind row. */
export type EnvironmentStage = {
  id: string;
  name: string;
  isBuiltin: boolean;
};

/** Canonical identity used for environment-kind storage and name lookup. */
export const normalizeEnvironmentKindName = (name: string): string => name.trim().toLowerCase();

export const isBuiltinEnvironmentKind = (name: string): name is BuiltinEnvironmentKind =>
  (BUILTIN_ENVIRONMENT_KINDS as readonly string[]).includes(normalizeEnvironmentKindName(name));

/**
 * Placeholder rendered in place of sensitive per-environment variable values.
 * The API masks sensitive values with this string in GET responses, and on
 * save substitutes the stored value for any incoming entry that equals it —
 * so the API and web MUST agree on the exact byte sequence. Single source
 * of truth; never redefine locally.
 */
export const ENV_VAR_MASK = '••••••••';
